import { useEffect, useRef } from 'react'
import type { DebouncedFuncLeading } from 'lodash'
import debounce from 'lodash/debounce'

import { Language, SearchLearningFeatureInput, useSearchLearningFeatureLazyQuery } from '@app/data'

const MIN_SEARCH_TEXT_LENGTH = 3
const DEBOUNCE_TIME = 500

export const useSearchLearningFeature = () => {
  const debouncedSearchRef = useRef<DebouncedFuncLeading<(props: SearchLearningFeatureInput) => void>>()

  const [searchLearningFeatureQuery, { error, data, loading }] = useSearchLearningFeatureLazyQuery({
    nextFetchPolicy: 'no-cache',
    fetchPolicy: 'no-cache'
  })

  const searchLearningFeatureFunction = (searchInput: SearchLearningFeatureInput) => {
    const {
      text,
      sourceLanguage = Language.CZECH,
      targetLanguage = Language.ENGLISH,
      size = 10,
      ...restProps
    } = searchInput

    if (text.length >= MIN_SEARCH_TEXT_LENGTH) {
      searchLearningFeatureQuery({
        variables: {
          searchLearningFeatureInput: { text: text.trim(), sourceLanguage, targetLanguage, size, ...restProps }
        }
      })
    }
  }

  const debouncedSearchLearningFeature = debounce(
    (props: SearchLearningFeatureInput) => searchLearningFeatureFunction(props),
    DEBOUNCE_TIME,
    { leading: true, trailing: true }
  )

  useEffect(() => {
    debouncedSearchRef.current = debouncedSearchLearningFeature

    return () => {
      debouncedSearchRef.current?.cancel()
    }
  }, [debouncedSearchLearningFeature])

  return {
    learningFeatures: data?.searchLearningFeature || [],
    error,
    loading,
    actions: {
      searchLearningFeature: (props: SearchLearningFeatureInput) => {
        debouncedSearchLearningFeature(props)
      }
    }
  }
}
